import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPostingsByIds } from '../../services/leverApi';
import type { InterviewLink } from '../../services/interviewLinksApi';

/** Lever posting titles by lower-cased posting id, for every posting the given links use. */
export function usePostingTitles(links: InterviewLink[]): Map<string, string> {
  const postingIds = useMemo(
    () => [...new Set(links.map((l) => l.postingId).filter((id): id is string => Boolean(id)))].sort(),
    [links]
  );
  const { data: postings } = useQuery({
    queryKey: ['lever-posting-titles', postingIds.join(',')],
    queryFn: () => fetchPostingsByIds(postingIds),
    enabled: postingIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => new Map((postings ?? []).map((p) => [p.id.toLowerCase(), p.title])), [postings]);
}

/**
 * What a role is called on screen: its Lever posting title. The link's own "Role title (shown to
 * the candidate)" is candidate-facing copy (often "First Interview | FINN"), so it only stands in
 * when Lever has no title for the posting.
 */
export function roleName(link: InterviewLink, postingTitles: Map<string, string>): string {
  const postingTitle = link.postingId ? postingTitles.get(link.postingId.toLowerCase()) : undefined;
  return postingTitle || link.jobTitle || link.name;
}
